import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { postNewThreadToApi, fetchAllThreadsFromApi, putNewRequestToThreadApi } from "../../api/ThreadsApi";
import dayjs from "dayjs";

type ThreadsState = {
    loaded: boolean | null;
    error: string;
    history: any[];
    newThreadId?: string;
    queryResponseLoading: boolean;
    floatingChatThreadId?: string;
}

const initialState: ThreadsState = {
    loaded: null,
    history: [],
    error: '',
    newThreadId: 'new',
    queryResponseLoading: false,
    floatingChatThreadId: 'new'
}

const threadsSlice = createSlice({
    name: "threads",
    initialState,
    reducers: {
        newRequestInThread: (state, action: PayloadAction<any>) => {
            const thread: any = state.history.find((t: any) => t.thread_id === action.payload.thread_id);
            if (thread) {
                thread.entries = [...thread.entries, {
                    thread_id: action.payload.thread_id,
                    created_at: dayjs().toISOString(),
                    category: "request",
                    data: action.payload.request
                }]
            }
        },
        addNewThread: (state, action: PayloadAction<any>) => {
            const maxId = state.history.reduce((max: number, item: any) => item.id > max ? item.id : max, -1);
            state.history = [{
                user_id: 1,
                id: maxId + 1,
                thread_id: action.payload.thread_id,
                entries: [action.payload.entry]
            }, ...state.history]
        },
        updateFloatingChatId: (state, action: PayloadAction<any>) => {
            state.floatingChatThreadId = action.payload.thread_id;
        },
        completedStreamingForEntry: (state, action: PayloadAction<any>) => {
            const { thread_id } = action.payload;
            const thread = state.history.find((t: any) => t.thread_id === thread_id);
            if (thread) {
                thread.entries.forEach((entry: any) => entry.isStreaming = false)
            }
        },
        resetNewChatId: (state) => {
            state.newThreadId = "new";
        }
    },
    extraReducers: builder => {
        builder
            // get all threads
            .addCase(fetchThreadHistory.pending, state => {
                state.loaded = false;
            })
            .addCase(fetchThreadHistory.fulfilled, (state, action) => {
                state.loaded = true;
                state.history = [...action.payload];
            })
            .addCase(fetchThreadHistory.rejected, (state, action) => {
                state.loaded = true;
                state.error = action.error.message || "Error loading thread history."
            })

            // handle new requests for existing thread
            .addCase(sendQueryToThread.pending, state => {
                state.queryResponseLoading = true;
            })
            .addCase(sendQueryToThread.fulfilled, (state, action: PayloadAction<any>) => {
                state.queryResponseLoading = false;
                const thread: any = state.history.find((t: any) => t.thread_id === action.payload.thread_id);
                if (thread) {
                    thread.entries = [...thread.entries, action.payload]
                }
                state.error = '';
            })
            .addCase(sendQueryToThread.rejected, (state, action) => {
                state.queryResponseLoading = false;
                state.error = action.error.message || "Error loading response from api."
            })

            //new thread 
            .addCase(initiateNewThread.pending, (state, action: PayloadAction<any>) => {
                state.queryResponseLoading = true;
            })
            .addCase(initiateNewThread.fulfilled, (state, action: PayloadAction<any>) => {
                state.queryResponseLoading = false;
                state.newThreadId = action.payload.thread_id;
                const thread: any = state.history.find((t: any) => t.thread_id === 'new');
                if (thread) {
                    thread.thread_id = action.payload.thread_id;
                    thread.entries = [...thread.entries, action.payload]
                    thread.entries.forEach((entry: any) => entry.thread_id = action.payload.thread_id)
                }
                state.error = '';
            })
            .addCase(initiateNewThread.rejected, (state) => {
                state.queryResponseLoading = false;
                state.error = "Unable to create new thread.";
            });
    }
});

export const fetchThreadHistory = createAsyncThunk(
    'threads/fetchThreadHistory',
    async () => {
        return await fetchAllThreadsFromApi();
    }
)

export const sendQueryToThread = createAsyncThunk(
    'threads/sendQueryToThread',
    async ({ threadId, request }: any, { dispatch }) => {
        dispatch(newRequestInThread({
            thread_id: threadId,
            request: request
        }))
        return await putNewRequestToThreadApi(threadId, request);
    }
)

export const initiateNewThread = createAsyncThunk(
    'threads/initiateNewThread',
    async (request: any, { dispatch }) => {
        const timestampNow = dayjs().toISOString();
        dispatch(addNewThread({
            created_at: timestampNow,
            thread_id: 'new',
            entry: {
                category: 'request',
                created_at: timestampNow,
                data: request.request
            }
        }))

        return await postNewThreadToApi(request.request);
    }
)

export const { newRequestInThread, addNewThread, updateFloatingChatId, completedStreamingForEntry, resetNewChatId } = threadsSlice.actions;
export default threadsSlice.reducer;

export const selectThreadHistory = (state: any) => state.allThreads.history;
export const selectIfThreadHistoryLoaded = (state: any) => state.allThreads.loaded;
export const selectThreadHistoryLoadingError = (state: any) => state.allThreads.error;
export const selectNewThreadId = (state: any) => state.allThreads.newThreadId;
export const selectIfAssistanceResponseLoading = (state: any) => state.allThreads.queryResponseLoading;
export const selectThreadFromHistoryById = (threadId: string) => (state: any) => {
    return threadId === "" ? null : state.allThreads.history.find((ch: any) => ch.thread_id === threadId);
};
export const selectFloatingChatActiveThreadId = (state: any) => state.allThreads.floatingChatThreadId;